import { api } from './api';

// Todas as leituras exigem tenant resolvido (header X-Store-Slug via api.js,
// populado pelo AuthContext.currentStore ou pelo AppWrapper quando em rota
// whitelabel).

// MariaDB armazena DataTypes.JSON como LONGTEXT e em alguns paths o getter
// do Sequelize não dispara — `images` chega como string crua tipo
// '["https://..."]', às vezes até dupla-encoded ('"[\\"...\\"]"'). Mesmo
// problema do theme da Store (ver normalizeStoreTheme). Forçamos parse
// defensivo iterativo no client.
function normalizeProductImages(p) {
  if (!p) return p;
  let imgs = p.images;
  for (let i = 0; i < 4 && typeof imgs === 'string'; i++) {
    try { imgs = JSON.parse(imgs); } catch { imgs = []; break; }
  }
  if (!Array.isArray(imgs)) imgs = [];
  return imgs === p.images ? p : { ...p, images: imgs };
}

function buildListQuery(filters = {}) {
  const qs = new URLSearchParams();
  if (filters.search) qs.set('q', filters.search);
  if (filters.q) qs.set('q', filters.q);
  if (filters.categoryId) qs.set('categoryId', filters.categoryId);
  if (filters.brand) qs.set('brand', filters.brand);
  if (filters.featured !== undefined && filters.featured !== '') qs.set('featured', String(filters.featured));
  if (filters.active !== undefined && filters.active !== '') qs.set('active', String(filters.active));
  if (filters.lowStock) qs.set('lowStock', 'true');
  if (filters.includeInactiveVariations) qs.set('includeInactiveVariations', 'true');
  if (filters.minPrice != null && filters.minPrice !== '') qs.set('minPrice', filters.minPrice);
  if (filters.maxPrice != null && filters.maxPrice !== '') qs.set('maxPrice', filters.maxPrice);
  if (filters.minRating != null && filters.minRating !== '') qs.set('minRating', filters.minRating);
  if (filters.publishedSince) qs.set('publishedSince', filters.publishedSince);
  if (filters.inStock) qs.set('inStock', 'true');
  if (filters.sort) qs.set('sort', filters.sort);
  if (filters.page) qs.set('page', filters.page);
  if (filters.limit) qs.set('limit', filters.limit);
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const productService = {
  getAll: async (filters = {}) => {
    const data = await api.get(`/api/products${buildListQuery(filters)}`);
    // Retrocompat: mocks devolviam array. Chamadores antigos podem esperar array.
    // Nova forma: { rows, count, page, limit, totalPages }. Devolvemos o novo shape;
    // callers antigos têm que se ajustar.
    if (data && Array.isArray(data.rows)) {
      return { ...data, rows: data.rows.map(normalizeProductImages) };
    }
    if (Array.isArray(data)) return data.map(normalizeProductImages);
    return data;
  },

  getById: async (idOrSlug) => {
    const data = await api.get(`/api/products/${encodeURIComponent(idOrSlug)}`);
    return normalizeProductImages(data);
  },

  // Legado: retornava produtos de um fornecedor específico. Agora listagem é
  // sempre no contexto do tenant atual (header X-Store-Slug).
  getBySupplier: async (_supplierIdOrSlug, filters = {}) => {
    return productService.getAll(filters);
  },

  create: async (payload) => {
    return api.post('/api/products', payload);
  },

  update: async (id, payload) => {
    return api.patch(`/api/products/${id}`, payload);
  },

  delete: async (id) => {
    return api.delete(`/api/products/${id}`);
  },

  duplicate: async (id) => {
    return api.post(`/api/products/${id}/duplicate`);
  },

  // Variations
  createVariation: async (productId, payload) => {
    return api.post(`/api/products/${productId}/variations`, payload);
  },
  updateVariation: async (id, payload) => {
    return api.patch(`/api/products/variations/${id}`, payload);
  },
  deleteVariation: async (id) => {
    return api.delete(`/api/products/variations/${id}`);
  }
};

export default productService;
